/**
 * UserProfileCard Component
 *
 * Displays the current user's profile information including:
 * - Avatar/initials
 * - Display name and email
 * - Identity provider badge
 * - Status indicator
 */
import React from 'react';
import {
  Card,
  CardHeader,
  Avatar,
  Text,
  Badge,
  Spinner,
  tokens,
  makeStyles,
} from '@fluentui/react-components';
import {
  PersonRegular,
  ShieldCheckmarkRegular,
} from '@fluentui/react-icons';
import { useCurrentUser } from '../hooks';
import type { User, IdentityProvider, UserStatus } from '../types';

const useStyles = makeStyles({
  card: {
    maxWidth: '400px',
    width: '100%',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  label: {
    color: tokens.colorNeutralForeground3,
    minWidth: '100px',
  },
  badges: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalS,
  },
  errorCard: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
  },
});

/**
 * Get the display color for identity provider
 */
function getIdpColor(idp: IdentityProvider): 'brand' | 'informative' | 'subtle' {
  switch (idp) {
    case 'AzureAD':
      return 'brand';
    case 'Okta':
    case 'Google':
    case 'GitHub':
      return 'informative';
    default:
      return 'subtle';
  }
}

/**
 * Get the display color for user status
 */
function getStatusColor(
  status: UserStatus
): 'success' | 'warning' | 'danger' | 'informative' {
  switch (status) {
    case 'Active':
      return 'success';
    case 'PendingVerification':
      return 'warning';
    case 'Suspended':
      return 'danger';
    default:
      return 'informative';
  }
}

/**
 * Get initials from display name
 */
function getInitials(displayName: string): string {
  const parts = displayName.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return displayName.substring(0, 2).toUpperCase();
}

export interface UserProfileCardProps {
  /** Optional user data to display (overrides useCurrentUser hook) */
  user?: User;
  /** Whether to show loading state */
  loading?: boolean;
  /** Error message to display */
  error?: string;
}

export const UserProfileCard: React.FC<UserProfileCardProps> = ({
  user: propUser,
  loading: propLoading,
  error: propError,
}) => {
  const styles = useStyles();
  const { data: hookUser, isLoading: hookLoading, error: hookError } = useCurrentUser();

  // Use props if provided, otherwise use hook data
  const user = propUser ?? hookUser;
  const loading = propLoading ?? hookLoading;
  const error = propError ?? (hookError ? hookError.message : undefined);

  if (loading) {
    return (
      <Card className={styles.card}>
        <CardHeader
          image={<Spinner size="small" />}
          header={<Text weight="semibold">Loading profile...</Text>}
        />
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={styles.card}>
        <div className={styles.errorCard}>
          <Text weight="semibold">Error loading profile</Text>
          <Text size={200}>{error}</Text>
        </div>
      </Card>
    );
  }

  if (!user) {
    return (
      <Card className={styles.card}>
        <CardHeader
          image={<PersonRegular />}
          header={<Text weight="semibold">Not signed in</Text>}
          description="Please sign in to view your profile"
        />
      </Card>
    );
  }

  return (
    <Card className={styles.card}>
      <CardHeader
        image={
          <Avatar
            name={user.displayName}
            initials={getInitials(user.displayName)}
            size={48}
            color="colorful"
          />
        }
        header={<Text weight="semibold" size={400}>{user.displayName}</Text>}
        description={user.email}
      />
      <div className={styles.content}>
        <div className={styles.row}>
          <Text className={styles.label} size={200}>
            Provider:
          </Text>
          <Badge
            appearance="outline"
            color={getIdpColor(user.identityProvider)}
            icon={<ShieldCheckmarkRegular />}
          >
            {user.identityProvider}
          </Badge>
        </div>
        <div className={styles.row}>
          <Text className={styles.label} size={200}>
            Status:
          </Text>
          <Badge appearance="filled" color={getStatusColor(user.status)}>
            {user.status}
          </Badge>
        </div>
        {user.lastLoginAt && (
          <div className={styles.row}>
            <Text className={styles.label} size={200}>
              Last login:
            </Text>
            <Text size={200}>
              {new Date(user.lastLoginAt).toLocaleDateString()}
            </Text>
          </div>
        )}
      </div>
    </Card>
  );
};
